import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sltAPI } from '../../services/api';
import { setSltViewAs } from '../../utils/sltViewAs';
import SLTLayout from './SLTLayout';
import { BRAND, cardStyle, tdStyle, thStyle, formatDate, StatPill, ClickableRow } from './sltUi';

const getYearSortValue = (yearName = '') => {
  const match = String(yearName).match(/\d+/);
  return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
};

// Group classes into Year → Subject → [classes], each level sorted.
const groupClassesByYear = (classes = []) => {
  const yearMap = new Map();
  classes.forEach((c) => {
    const year = c.yearGroup || 'Year not set';
    const subject = c.subject || 'No subject';
    if (!yearMap.has(year)) yearMap.set(year, new Map());
    const subjectMap = yearMap.get(year);
    if (!subjectMap.has(subject)) subjectMap.set(subject, []);
    subjectMap.get(subject).push(c);
  });
  return Array.from(yearMap.entries())
    .sort((a, b) => getYearSortValue(a[0]) - getYearSortValue(b[0]) || a[0].localeCompare(b[0]))
    .map(([year, subjectMap]) => ({
      year,
      classCount: Array.from(subjectMap.values()).reduce((sum, list) => sum + list.length, 0),
      subjects: Array.from(subjectMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([subject, list]) => ({
          subject,
          classes: list.sort((a, b) => String(a.name).localeCompare(String(b.name)))
        }))
    }));
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(Number(bytes)) || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SLTTeacherDetail = ({ user, onLogout }) => {
  const { teacherUid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // Per-plan source documents: { [planId]: { loading, error, open, documents } }
  const [planDocs, setPlanDocs] = useState({});

  useEffect(() => {
    let isMounted = true;
    sltAPI.getTeacherDetail(teacherUid)
      .then((res) => { if (isMounted) setData(res.data); })
      .catch((err) => {
        if (isMounted) setError(err?.response?.data?.error || 'Unable to load this teacher.');
      });
    return () => { isMounted = false; };
  }, [teacherUid]);

  const teacher = data?.teacher;
  const classesByYear = useMemo(() => groupClassesByYear(data?.classes), [data]);

  // Open a page of the teacher's own workspace in read-only view-as mode.
  const exploreTeacherView = (path) => {
    setSltViewAs({ teacherUid, teacherName: teacher?.name || 'this teacher' });
    navigate(path);
  };

  // Lesson plans open as the class roster (/students) the teacher runs the plan with.
  const exploreLessonPlan = (lessonPlanId) => {
    const schedule = (data?.classes || []).find((c) => String(c.lessonPlanId) === String(lessonPlanId));
    exploreTeacherView(schedule ? `/students/${schedule._id}` : `/lessons/${lessonPlanId}`);
  };

  // Download the source document(s) the teacher uploaded to generate the plan.
  // One document downloads immediately; several expand into a file list.
  const handleDownloadDocs = async (planId) => {
    const existing = planDocs[planId];
    if (existing?.documents) {
      if (existing.documents.length === 1 && existing.documents[0].downloadUrl) {
        window.open(existing.documents[0].downloadUrl, '_blank', 'noopener');
      } else {
        setPlanDocs((prev) => ({ ...prev, [planId]: { ...existing, open: !existing.open } }));
      }
      return;
    }
    if (existing?.loading) return;

    setPlanDocs((prev) => ({ ...prev, [planId]: { loading: true } }));
    try {
      const res = await sltAPI.getLessonPlanDocuments(planId);
      const documents = res.data?.documents || [];
      if (documents.length === 1 && documents[0].downloadUrl) {
        window.open(documents[0].downloadUrl, '_blank', 'noopener');
      }
      setPlanDocs((prev) => ({ ...prev, [planId]: { documents, open: documents.length > 1 } }));
    } catch (err) {
      setPlanDocs((prev) => ({ ...prev, [planId]: { error: 'Could not load documents.' } }));
    }
  };

  return (
    <SLTLayout
      user={user}
      onLogout={onLogout}
      title={teacher ? teacher.name : 'Teacher'}
      subtitle={teacher ? `${teacher.email} · joined ${formatDate(teacher.createdAt)} · ${teacher.studentCount} students on roster` : undefined}
      breadcrumbs={[
        { label: 'Teachers', to: '/slt/teachers' },
        { label: teacher ? teacher.name : '…' }
      ]}
    >
      {error && <p style={{ color: '#B3261E', fontWeight: 700 }}>{error}</p>}
      {!data && !error && <p style={{ color: BRAND.muted }}>Loading teacher…</p>}

      {teacher && (
        <>
          <section style={{ ...cardStyle, marginBottom: '22px' }}>
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 10px' }}>Classes ({(data.classes || []).length})</h2>
            {(data.classes || []).length === 0 && <p style={{ color: BRAND.muted, margin: 0 }}>No classes created yet.</p>}
            <style>{`
              .slt-year-group summary, .slt-subject-group summary { cursor: pointer; }
              .slt-year-group summary::-webkit-details-marker, .slt-subject-group summary::-webkit-details-marker { display: none; }
              .slt-year-group summary:hover, .slt-subject-group summary:hover { color: ${BRAND.coral}; }
              .slt-year-group[open] > summary .slt-caret, .slt-subject-group[open] > summary .slt-caret { transform: rotate(90deg); }
              .slt-caret { display: inline-block; transition: transform 0.15s ease; }
              .slt-class-chip { transition: background 0.15s ease, color 0.15s ease; }
              .slt-class-chip:hover { background: ${BRAND.teal} !important; color: ${BRAND.white} !important; }
              table.slt-strong-hover .slt-row.clickable td { transition: background 0.15s ease, color 0.15s ease; }
              table.slt-strong-hover .slt-row.clickable:hover td { background: ${BRAND.teal}; }
              table.slt-strong-hover .slt-row.clickable:hover td,
              table.slt-strong-hover .slt-row.clickable:hover td * { color: ${BRAND.white} !important; }
              /* Keep the in-row download chip visibly its own control on a teal row */
              table.slt-strong-hover .slt-row.clickable:hover .slt-class-chip {
                background: ${BRAND.coral} !important;
                border-color: ${BRAND.white} !important;
              }
              table.slt-strong-hover .slt-row.clickable:hover .slt-class-chip:hover {
                background: ${BRAND.white} !important;
                color: ${BRAND.coral} !important;
              }
            `}</style>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {classesByYear.map((yearGroup) => (
                <details key={yearGroup.year} className="slt-year-group" style={{ background: BRAND.cream, border: '1.5px solid rgba(45,64,66,0.2)', borderRadius: '12px', padding: '10px 14px' }}>
                  <summary style={{ alignItems: 'center', display: 'flex', gap: '8px', fontFamily: 'Nunito, sans-serif', fontSize: '1rem', fontWeight: 800, listStyle: 'none' }}>
                    <span className="slt-caret" aria-hidden="true">▸</span>
                    {yearGroup.year}
                    <span style={{ color: BRAND.muted, fontSize: '0.85rem', fontWeight: 700 }}>
                      ({yearGroup.classCount} {yearGroup.classCount === 1 ? 'class' : 'classes'})
                    </span>
                  </summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
                    {yearGroup.subjects.map((subjectGroup) => (
                      <details key={subjectGroup.subject} className="slt-subject-group" open style={{ borderLeft: `3px solid ${BRAND.teal}`, marginLeft: '8px', paddingLeft: '12px' }}>
                        <summary style={{ alignItems: 'center', display: 'flex', gap: '7px', fontSize: '0.9rem', fontWeight: 800, listStyle: 'none' }}>
                          <span className="slt-caret" aria-hidden="true">▸</span>
                          {subjectGroup.subject}
                          <span style={{ color: BRAND.muted, fontWeight: 700 }}>({subjectGroup.classes.length})</span>
                        </summary>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '8px 0 4px' }}>
                          {subjectGroup.classes.map((c) => (
                            <button
                              key={c._id}
                              type="button"
                              className="slt-class-chip"
                              onClick={() => exploreTeacherView(`/students/${c._id}`)}
                              title={`Open ${teacher.name}'s view of ${c.name} (read-only)`}
                              style={{ background: 'rgba(117, 185, 190, 0.18)', border: `1.5px solid ${BRAND.teal}`, borderRadius: '999px', color: BRAND.charcoal, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 800, padding: '5px 13px' }}
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                      </details>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section style={{ ...cardStyle, marginBottom: '22px', overflowX: 'auto', padding: 0 }}>
            <h2 style={{ fontSize: '1.15rem', margin: 0, padding: '18px 20px 8px' }}>Teacher Activity ({(data.homeworks || []).length})</h2>
            {(data.homeworks || []).length === 0
              ? <p style={{ color: BRAND.muted, padding: '0 20px 18px' }}>No homework generated yet.</p>
              : (
                <table className="slt-strong-hover" style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Homework</th>
                      <th style={thStyle}>Lesson plan</th>
                      <th style={thStyle}>Class</th>
                      <th style={thStyle}>Duration</th>
                      <th style={thStyle}>Students</th>
                      <th style={thStyle}>Tutorials</th>
                      <th style={thStyle}>Attempts</th>
                      <th style={thStyle}>Accuracy</th>
                      <th style={thStyle}>Published</th>
                      <th style={thStyle} aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.homeworks.map((hw) => (
                      <ClickableRow key={hw.homeworkId} onActivate={() => exploreTeacherView(`/homework/${hw.homeworkId}`)}>
                        <td style={{ ...tdStyle, fontWeight: 800, whiteSpace: 'nowrap' }}>{hw.label}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 700 }}>{hw.lessonPlanTitle}</div>
                          {hw.subject && <div style={{ color: BRAND.muted, fontSize: '0.8rem' }}>{hw.subject}</div>}
                        </td>
                        <td style={tdStyle}>{hw.className}{hw.yearGroup ? ` · ${hw.yearGroup}` : ''}</td>
                        <td style={tdStyle}>{hw.durationMinutes ? `${hw.durationMinutes} min` : '—'}</td>
                        <td style={tdStyle}>{hw.studentsAssigned}</td>
                        <td style={tdStyle}>{hw.tutorialsGenerated}{hw.tutorialsCompleted ? ` (${hw.tutorialsCompleted} done)` : ''}</td>
                        <td style={tdStyle}>{hw.attempts}</td>
                        <td style={tdStyle}><StatPill value={hw.averageAccuracy} /></td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(hw.createdAt)}</td>
                        <td style={{ ...tdStyle, color: BRAND.muted, textAlign: 'right' }} aria-hidden="true">›</td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
              )}
          </section>

          <section style={{ ...cardStyle, overflowX: 'auto', padding: 0 }}>
            <h2 style={{ fontSize: '1.15rem', margin: 0, padding: '18px 20px 8px' }}>Lesson plans ({(data.lessonPlans || []).length})</h2>
            {(data.lessonPlans || []).length === 0
              ? <p style={{ color: BRAND.muted, padding: '0 20px 18px' }}>No lesson plans created yet.</p>
              : (
                <table className="slt-strong-hover" style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Title</th>
                      <th style={thStyle}>Subject</th>
                      <th style={thStyle}>Year group</th>
                      <th style={thStyle}>Lessons</th>
                      <th style={thStyle}>Created</th>
                      <th style={thStyle}>Source document</th>
                      <th style={thStyle} aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.lessonPlans.map((lp) => {
                      const docsState = planDocs[lp._id] || {};
                      return (
                        <React.Fragment key={lp._id}>
                          <ClickableRow onActivate={() => exploreLessonPlan(lp._id)}>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>{lp.title}</td>
                            <td style={tdStyle}>{lp.subject}</td>
                            <td style={tdStyle}>{lp.yearGroup}</td>
                            <td style={tdStyle}>{lp.numberOfLessons}</td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(lp.createdAt)}</td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                              {docsState.documents && docsState.documents.length === 0
                                ? <span style={{ color: BRAND.muted }}>No documents</span>
                                : (
                                  <button
                                    type="button"
                                    className="slt-class-chip"
                                    onClick={(e) => { e.stopPropagation(); handleDownloadDocs(lp._id); }}
                                    title="Download the document(s) uploaded to generate this plan"
                                    style={{ background: 'rgba(117, 185, 190, 0.18)', border: `1.5px solid ${BRAND.teal}`, borderRadius: '999px', color: BRAND.charcoal, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 800, padding: '4px 12px' }}
                                  >
                                    {docsState.loading ? 'Loading…' : (docsState.documents && docsState.documents.length > 1 ? `⬇ ${docsState.documents.length} files` : '⬇ Download')}
                                  </button>
                                )}
                              {docsState.error && <span style={{ color: '#B3261E', fontSize: '0.8rem', marginLeft: 8 }}>{docsState.error}</span>}
                            </td>
                            <td style={{ ...tdStyle, color: BRAND.muted, textAlign: 'right' }} aria-hidden="true">›</td>
                          </ClickableRow>
                          {docsState.open && (docsState.documents || []).length > 1 && (
                            <tr>
                              <td colSpan={7} style={{ ...tdStyle, background: BRAND.cream, padding: '10px 20px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  {docsState.documents.map((doc) => (
                                    <div key={doc.documentId} style={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '0.88rem' }}>
                                      {doc.downloadUrl
                                        ? <a href={doc.downloadUrl} target="_blank" rel="noreferrer" style={{ color: BRAND.charcoal, fontWeight: 800 }}>⬇ {doc.originalFilename}</a>
                                        : <span style={{ color: BRAND.muted, fontWeight: 700 }}>{doc.originalFilename} (unavailable)</span>}
                                      {doc.lessonTag && <span style={{ color: BRAND.muted }}>{doc.lessonTag}</span>}
                                      <span style={{ color: BRAND.muted }}>{formatFileSize(doc.fileSize)}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </section>
        </>
      )}
    </SLTLayout>
  );
};

export default SLTTeacherDetail;
